import { create } from "zustand";
import { api, ApiError } from "@/lib/api";
import type {
  User,
  CompanyWithRole,
  Property,
  MeResponse,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
} from "@/types";

interface AuthState {
  user: User | null;
  companies: CompanyWithRole[];
  properties: Property[];
  isSystemAdmin: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;

  loadUser: () => Promise<void>;
  login: (data: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  reset: () => void;
}

const initialState = {
  user: null,
  companies: [],
  properties: [],
  isSystemAdmin: false,
  isAuthenticated: false,
  isLoading: true,
};

export const useAuthStore = create<AuthState>((set) => ({
  ...initialState,

  loadUser: async () => {
    const { isAuthenticated } = useAuthStore.getState();
    // Only show loading screen on initial boot, not on subsequent refreshes
    if (!isAuthenticated) {
      set({ isLoading: true });
    }
    try {
      const res = await api.get<MeResponse>("/auth/me");
      set({
        user: res.data.user,
        companies: res.data.companies,
        properties: res.data.properties,
        isSystemAdmin: res.data.user.isSystemAdmin,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        set({ ...initialState, isLoading: false });
      } else {
        set({ isLoading: false });
      }
    }
  },

  login: async (data: LoginRequest) => {
    const res = await api.post<LoginResponse>("/auth/login", data);
    set({
      user: res.data.user,
      isAuthenticated: true,
    });
    // Load full user context (companies, properties)
    const meRes = await api.get<MeResponse>("/auth/me");
    set({
      companies: meRes.data.companies,
      properties: meRes.data.properties,
      isSystemAdmin: meRes.data.user.isSystemAdmin,
    });
  },

  register: async (data: RegisterRequest) => {
    await api.post<RegisterResponse>("/auth/register", data);
    // After registration, user needs to verify email before logging in
  },

  logout: async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Best-effort logout -- clear local state even if API call fails
    }
    set({ ...initialState, isLoading: false });
  },

  reset: () => {
    set({ ...initialState, isLoading: false });
  },
}));
